import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faArrowRightArrowLeft, faCheck, faSliders } from "@fortawesome/free-solid-svg-icons";
import {
  DialogFrame,
  Dialog_VM,
} from "../../../components/Dialogs/dialog-base";
import { ViewUploadZone } from "./view-upload-zone.component";
import { PPCMCalibrationPanel } from "./ppcm-calibration-panel.component";
import { CustomInput } from "../../../components/Input/CustomInput";
import { InputLabel } from "../../../components/Input/InputLabel";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import LoadingElement from "../../../components/loading-element/loading-element";
import { CustomStyleUploadData, StyleCategory } from "../../../../data/repositories/StyleRepository";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { useTranslation } from "react-i18next";

interface CustomStyleUploadDialogProps {
  dialogOption: Dialog_VM;
  onSubmit: (data: CustomStyleUploadData) => void;
  isLoading: boolean;
}

export function CustomStyleUploadDialog({
  dialogOption,
  onSubmit,
  isLoading,
}: CustomStyleUploadDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const { styleRepository } = useRepositoriesContext();
  const [brandName, setBrandName] = useState("");
  const [modelName, setModelName] = useState("");
  const [categoryKey, setCategoryKey] = useState("");
  const [categories, setCategories] = useState<StyleCategory[]>([]);
  const [frontImage, setFrontImage] = useState<File | null>(null);
  const [backImage, setBackImage] = useState<File | null>(null);
  const [leftImage, setLeftImage] = useState<File | null>(null);
  const [rightImage, setRightImage] = useState<File | null>(null);
  const [mirrorLeftToRight, setMirrorLeftToRight] = useState(false);
  const [mirrorRightToLeft, setMirrorRightToLeft] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [leftPreviewUrl, setLeftPreviewUrl] = useState<string | null>(null);
  const [rightPreviewUrl, setRightPreviewUrl] = useState<string | null>(null);
  const [showCalibration, setShowCalibration] = useState(false);
  const [calibratedPpcm, setCalibratedPpcm] = useState<number | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCategoryDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedCategory = categories.find(c => c.key === categoryKey);

  useEffect(() => {
    styleRepository.getStyleCategories().then((cats: StyleCategory[]) => {
      setCategories(cats);
      if (cats.length > 0 && !categoryKey) {
        setCategoryKey(cats[0].key);
      }
    });
  }, []);

  const formIsComplete = brandName.trim() !== "" && modelName.trim() !== "" && categoryKey !== "" && frontImage !== null;
  const isReadyToSubmit = formIsComplete && calibratedPpcm !== null;

  function handleSubmit() {
    if (!isReadyToSubmit || !frontImage || !calibratedPpcm) return;

    onSubmit({
      brandName: brandName.trim(),
      modelName: modelName.trim(),
      categoryKey,
      frontImage,
      backImage: backImage || undefined,
      leftImage: leftImage || undefined,
      rightImage: rightImage || undefined,
      mirrorLeftToRight: mirrorLeftToRight && leftImage !== null && rightImage === null,
      mirrorRightToLeft: mirrorRightToLeft && rightImage !== null && leftImage === null,
      ppcm: calibratedPpcm,
    });
  }

  function handleClose() {
    // Revoke preview URLs to prevent memory leaks
    if (leftPreviewUrl) URL.revokeObjectURL(leftPreviewUrl);
    if (rightPreviewUrl) URL.revokeObjectURL(rightPreviewUrl);

    // Reset form state when closing
    setBrandName("");
    setModelName("");
    setCategoryKey(categories.length > 0 ? categories[0].key : "");
    setFrontImage(null);
    setBackImage(null);
    setLeftImage(null);
    setRightImage(null);
    setMirrorLeftToRight(false);
    setMirrorRightToLeft(false);
    setLeftPreviewUrl(null);
    setRightPreviewUrl(null);
    setShowCalibration(false);
    setCalibratedPpcm(null);
  }

  function handleFrontImageSelect(file: File | null) {
    setFrontImage(file);
    // Reset calibration when front image changes
    if (!file || file !== frontImage) {
      setCalibratedPpcm(null);
    }
  }

  function handleCalibrationComplete(ppcm: number) {
    setCalibratedPpcm(ppcm);
    setShowCalibration(false);
  }

  function handleCalibrationBack() {
    // Just go back to form without clearing anything
    setShowCalibration(false);
  }

  function handleLeftImageSelect(file: File | null) {
    // Revoke old preview URL
    if (leftPreviewUrl) URL.revokeObjectURL(leftPreviewUrl);

    setLeftImage(file);
    if (file) {
      setLeftPreviewUrl(URL.createObjectURL(file));
    } else {
      setLeftPreviewUrl(null);
      setMirrorLeftToRight(false);
    }
  }

  function handleRightImageSelect(file: File | null) {
    // Revoke old preview URL
    if (rightPreviewUrl) URL.revokeObjectURL(rightPreviewUrl);

    setRightImage(file);
    if (file) {
      setRightPreviewUrl(URL.createObjectURL(file));
    } else {
      setRightPreviewUrl(null);
      setMirrorRightToLeft(false);
    }
  }

  return (
    <DialogFrame dialogOption={dialogOption} onClose={handleClose}>
      {showCalibration && frontImage ? (
        <PPCMCalibrationPanel
          imageFile={frontImage}
          onCalibrated={handleCalibrationComplete}
          onBack={handleCalibrationBack}
          initialPpcm={22}
        />
      ) : (
      <div className="p-6 min-w-[500px] max-w-[700px]">
        {isLoading && <LoadingElement transparent className="rounded" />}

        <h2 className="text-lg font-bold mb-4">
          {t("designListStyle.uploadCustomStyle")}
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          {t("designListStyle.uploadCustomStyleDescription")}
        </p>

        {/* Brand, Model, and Category inputs */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div>
            <InputLabel>
              {t("designListStyle.brand")}{" "}
              <span className="text-red-500">*</span>
            </InputLabel>
            <CustomInput
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder={t("designListStyle.enterBrandName")}
              maxLength={50}
              className="w-full"
            />
          </div>
          <div>
            <InputLabel>
              {t("model")} <span className="text-red-500">*</span>
            </InputLabel>
            <CustomInput
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              placeholder={t("designListStyle.enterModelName")}
              maxLength={50}
              className="w-full"
            />
          </div>
          <div ref={dropdownRef} className="relative">
            <InputLabel>
              {t("designListStyle.category")}{" "}
              <span className="text-red-500">*</span>
            </InputLabel>
            <button
              type="button"
              onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
              className="w-full h-9 px-3 text-sm bg-gray-100 border border-gray-200 rounded text-gray-700 focus:outline-none focus:border-gray-600 cursor-pointer flex items-center justify-between"
            >
              <span>
                {selectedCategory?.name ||
                  t("designListStyle.selectCategory")}
              </span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`text-gray-500 text-xs transition-transform ${categoryDropdownOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {categoryDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-auto">
                {categories.map((cat) => (
                  <div
                    key={cat.key}
                    onClick={() => {
                      setCategoryKey(cat.key);
                      setCategoryDropdownOpen(false);
                    }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                      cat.key === categoryKey ? 'bg-gray-100 font-medium' : ''
                    }`}
                  >
                    {cat.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Image upload zones */}
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 mb-3">
            {t("designListStyle.productImages")}
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col">
              <ViewUploadZone
                viewName={t("designListStyle.viewFront")}
                required={true}
                file={frontImage}
                onFileSelect={handleFrontImageSelect}
              />
              {/* Calibration status */}
              {frontImage && calibratedPpcm !== null && (
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <FontAwesomeIcon icon={faCheck} size="xs" />
                    {t("designListStyle.sizeCalibrated")}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowCalibration(true)}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <FontAwesomeIcon icon={faSliders} size="xs" />
                    {t("designListStyle.recalibrate")}
                  </button>
                </div>
              )}
            </div>
            <ViewUploadZone
              viewName={t("designListStyle.viewBack")}
              required={false}
              file={backImage}
              onFileSelect={setBackImage}
            />
            <ViewUploadZone
              viewName={t("designListStyle.viewLeft")}
              required={false}
              file={leftImage}
              onFileSelect={handleLeftImageSelect}
              disabled={mirrorRightToLeft}
              mirroredPreviewUrl={mirrorRightToLeft ? rightPreviewUrl : null}
            />
            <ViewUploadZone
              viewName={t("designListStyle.viewRight")}
              required={false}
              file={rightImage}
              onFileSelect={handleRightImageSelect}
              disabled={mirrorLeftToRight}
              mirroredPreviewUrl={mirrorLeftToRight ? leftPreviewUrl : null}
            />
          </div>

          {/* Mirroring option - show when only Left or Right is uploaded */}
          {leftImage && !rightImage && !mirrorRightToLeft && (
            <label className="flex items-center gap-2 mt-3 cursor-pointer text-sm text-gray-700 hover:text-gray-900">
              <input
                type="checkbox"
                checked={mirrorLeftToRight}
                onChange={(e) => setMirrorLeftToRight(e.target.checked)}
                className="w-4 h-4 accent-primary"
              />
              <FontAwesomeIcon icon={faArrowRightArrowLeft} className="text-gray-500" />
              <span>{t("designListStyle.mirrorLeftToRight")}</span>
            </label>
          )}
          {rightImage && !leftImage && !mirrorLeftToRight && (
            <label className="flex items-center gap-2 mt-3 cursor-pointer text-sm text-gray-700 hover:text-gray-900">
              <input
                type="checkbox"
                checked={mirrorRightToLeft}
                onChange={(e) => setMirrorRightToLeft(e.target.checked)}
                className="w-4 h-4 accent-primary"
              />
              <FontAwesomeIcon icon={faArrowRightArrowLeft} className="text-gray-500" />
              <span>{t("designListStyle.mirrorRightToLeft")}</span>
            </label>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <SecondaryButton
            onClick={() => {
              handleClose();
              dialogOption.closeDialog();
            }}
            disabled={isLoading}
          >
            {t("cancel")}
          </SecondaryButton>
          {formIsComplete && !calibratedPpcm ? (
            <PrimaryButton onClick={() => setShowCalibration(true)}>
              {t("designListStyle.continueToCalibration")}
            </PrimaryButton>
          ) : (
            <PrimaryButton
              onClick={handleSubmit}
              disabled={!isReadyToSubmit || isLoading}
            >
              {t("designListStyle.createStyle")}
            </PrimaryButton>
          )}
        </div>
      </div>
      )}
    </DialogFrame>
  );
}
